import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Users, CreditCard, Clock, Search, Shield, Pause, Play, MapPin, Loader2,
  Eye, KeyRound, Trash2, UserCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const PLANS = ["Trial", "Solo", "Team", "Premium", "Standard"];

const adminReq = async (path, { method = "GET", body } = {}) => {
  const res = await fetch(`${API}/api/admin${path}`, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.detail || "Action failed");
  return json;
};

export default function PlatformAdmin() {
  const { currentUser, beginViewAs } = useFgp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [stats, setStats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = useCallback(async () => {
    try {
      const [w, s, sub] = await Promise.all([
        adminReq("/workspaces"), adminReq("/stats"), adminReq("/subscriptions"),
      ]);
      setWorkspaces(w); setStats(s); setSubs(sub);
    } catch {
      toast.error("Could not load platform data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openWorkspace = async (w) => {
    setSelected(w); setMembers([]); setDeleteText("");
    setMembersLoading(true);
    try { setMembers(await adminReq(`/workspaces/${w.id}/members`)); }
    catch { toast.error("Could not load members"); }
    finally { setMembersLoading(false); }
  };

  const doAction = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await load(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const suspend = (w) => doAction(async () => {
    await adminReq(`/workspaces/${w.id}/suspend`, { method: "POST" });
    setSelected((s) => s && { ...s, status: "suspended" });
  }, `${w.name} suspended`);

  const reactivate = (w) => doAction(async () => {
    await adminReq(`/workspaces/${w.id}/reactivate`, { method: "POST" });
    setSelected((s) => s && { ...s, status: "active" });
  }, `${w.name} reactivated`);

  const changePlan = (w, plan) => doAction(async () => {
    await adminReq(`/workspaces/${w.id}/plan`, { method: "PATCH", body: { plan } });
    setSelected((s) => s && { ...s, plan });
  }, `Plan changed to ${plan}`);

  const submitReset = () => doAction(async () => {
    if (resetPw.trim().length < 6) throw new Error("Password must be at least 6 characters");
    await adminReq(`/workspaces/${selected.id}/members/${resetTarget.id}/reset-password`, {
      method: "POST", body: { new_password: resetPw },
    });
    setResetTarget(null); setResetPw("");
  }, "Password reset — the member must sign in again");

  const deleteWorkspace = (w) => doAction(async () => {
    await adminReq(`/workspaces/${w.id}?confirm_name=${encodeURIComponent(deleteText)}`, { method: "DELETE" });
    setSelected(null);
  }, `${w.name} deleted`);

  const startViewAs = async (w) => {
    setBusy(true);
    try {
      await beginViewAs(w.id);
      toast.success(`Now viewing ${w.name} (read only)`);
      navigate("/app/dashboard");
    } catch (e) { toast.error(e.message || "Could not open workspace"); }
    finally { setBusy(false); }
  };

  if (!currentUser?.isPlatformAdmin) return <Navigate to="/app/dashboard" replace />;

  const icons = [Building2, Users, CreditCard, Clock];
  const filtered = workspaces.filter((w) =>
    (w.name || "").toLowerCase().includes(q.toLowerCase()) || (w.owner || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-600 bg-neutral-50 px-2.5 py-1 rounded-full"><Shield className="h-3 w-3" /> Platform Owner</span>
      </div>
      <PageHeader title="Control Room" subtitle="Oversee and control every workspace, subscription and user across Swishr Central." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s, i) => (
          <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub} icon={icons[i]} tone={i === 0 ? "blue" : i === 3 ? "amber" : "default"} testid={`admin-stat-${i}`} />
        ))}
      </div>

      {/* Subscription / plan breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6" data-testid="admin-subscriptions">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-4 w-4 text-neutral-500" />
          <h3 className="font-heading font-semibold text-slate-900">Subscriptions by plan</h3>
          <span className="text-xs text-slate-400">(Stripe billing coming soon)</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {subs.map((p) => (
            <div key={p.plan} className="rounded-lg border border-slate-200 p-3" data-testid={`sub-card-${p.plan}`}>
              <p className="text-xs text-slate-400">{p.plan}</p>
              <p className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">{p.count}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workspaces or owners…" className="pl-9 bg-white" data-testid="admin-search" />
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead>Workspace</TableHead>
              <TableHead className="hidden sm:table-cell">Owner</TableHead>
              <TableHead className="hidden md:table-cell">Plan</TableHead>
              <TableHead className="hidden lg:table-cell">Users</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading workspaces…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400" data-testid="admin-empty">No workspaces yet — new sign-ups will appear here.</TableCell></TableRow>
            )}
            {filtered.map((w) => (
              <TableRow key={w.id} className="cursor-pointer" onClick={() => openWorkspace(w)} data-testid={`workspace-row-${w.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500"><Building2 className="h-4 w-4" /></div>
                    <div><p className="text-sm font-medium text-slate-800">{w.name}{w.isDemo && <span className="ml-2 text-[10px] font-semibold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Demo</span>}</p><p className="text-xs text-slate-400">{w.region}</p></div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-slate-500 text-sm">{w.owner}</TableCell>
                <TableCell className="hidden md:table-cell"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{w.plan}</span></TableCell>
                <TableCell className="hidden lg:table-cell text-slate-600 text-sm">{w.users} user{w.users === 1 ? "" : "s"}</TableCell>
                <TableCell><StatusBadge status={w.status} /></TableCell>
                <TableCell><span className="text-xs text-neutral-600 font-medium">Manage</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      {/* Workspace management dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="workspace-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-neutral-50 text-neutral-600 flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
                  <div>
                    <DialogTitle className="font-heading">{selected.name}</DialogTitle>
                    <DialogDescription className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {selected.region} · owner {selected.owner}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-3 my-2">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-400">Status</p>
                  <div className="mt-1"><StatusBadge status={selected.status} /></div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-400">Plan</p>
                  <Select value={PLANS.includes(selected.plan) ? selected.plan : undefined} onValueChange={(v) => changePlan(selected, v)}>
                    <SelectTrigger className="mt-1 h-8 text-sm" data-testid="admin-plan-select"><SelectValue placeholder={selected.plan} /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map((p) => <SelectItem key={p} value={p} data-testid={`plan-opt-${p}`}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-400">Users</p>
                  <p className="text-sm font-medium text-slate-800 mt-2">{selected.users}</p>
                </div>
              </div>

              {/* Primary controls */}
              <div className="flex flex-wrap gap-2">
                {selected.status === "suspended" ? (
                  <Button onClick={() => reactivate(selected)} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="admin-activate"><Play className="h-4 w-4" /> Reactivate</Button>
                ) : (
                  <Button onClick={() => suspend(selected)} disabled={busy} variant="outline" className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="admin-suspend"><Pause className="h-4 w-4" /> Suspend</Button>
                )}
                <Button onClick={() => startViewAs(selected)} disabled={busy || selected.status === "suspended"} variant="outline" className="gap-1.5" data-testid="admin-view-as"><Eye className="h-4 w-4" /> View as</Button>
              </div>

              {/* Members */}
              <div className="mt-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Members</p>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {membersLoading && <div className="p-4 text-center text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>}
                  {!membersLoading && members.length === 0 && <div className="p-4 text-center text-sm text-slate-400">No members.</div>}
                  {members.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 p-3" data-testid={`member-row-${m.id}`}>
                      <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center overflow-hidden">
                        {m.avatar ? <img src={m.avatar} alt="" className="h-full w-full object-cover" /> : <UserCircle2 className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{m.name || "—"}</p>
                        <p className="text-xs text-slate-400 truncate">{m.email} · {m.role.replace("_", " ")}</p>
                      </div>
                      {m.authProvider === "google" ? (
                        <span className="text-[10px] text-slate-400">Google account</span>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => { setResetTarget(m); setResetPw(""); }} className="gap-1 text-xs" data-testid={`member-reset-${m.id}`}><KeyRound className="h-3.5 w-3.5" /> Reset password</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Danger zone */}
              {!selected.isDemo && (
                <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-rose-500 mb-1">Danger zone</p>
                  <p className="text-xs text-slate-500 mb-2">Type <b>{selected.name}</b> to permanently delete this workspace and all its data.</p>
                  <div className="flex gap-2">
                    <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder={selected.name} className="h-9 bg-white" data-testid="admin-delete-confirm-input" />
                    <Button onClick={() => deleteWorkspace(selected)} disabled={busy || deleteText !== selected.name} variant="outline" className="gap-1.5 text-rose-600 border-rose-300 hover:bg-rose-100 shrink-0" data-testid="admin-delete-confirm"><Trash2 className="h-4 w-4" /> Delete</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password sub-dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset password</DialogTitle>
            <DialogDescription>Set a new password for {resetTarget?.email}. They will be signed out of all sessions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="At least 6 characters" data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={submitReset} disabled={busy || resetPw.length < 6} data-testid="reset-pw-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
